/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet-async';

import { Button, Typography } from '@coursera/cds-core';
import { AnswerWrongIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/enterprise-ui';

const NotFound = ({ description, children }: React.PropsWithChildren<{ description: string }>) => (
  <div
    css={(theme) => ({
      padding: 128,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 'var(--cds-spacing-200)',
    })}
    data-testid="not-found"
  >
    <AnswerWrongIcon size="large" css={{ transform: 'scale(2)', margin: 16 }} color="warning" />
    {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
    <Typography variant="h1semibold">{_t('Sorry')}</Typography>
    {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
    <Typography variant="h2" color="supportText">
      {description}
    </Typography>
    {children}
    <Helmet
      meta={[
        {
          name: 'robots',
          content: 'noindex, nofollow',
        },
      ]}
    />
  </div>
);

export const PageNotFound = () => (
  <NotFound description={_t('Looks like you found a page that does not exist or the URL was mistyped by accident.')} />
);

export const OrgNotFound = () => (
  <NotFound
    description={_t('Looks like you found an organization that does not exist or the URL was mistyped by accident.')}
  />
);

export const SkillsetNotFound = () => (
  <NotFound
    description={_t('Looks like you found an SkillSet that does not exist or the URL was mistyped by accident.')}
  />
);

export const ProgramNotFound = () => (
  <NotFound
    description={_t('Looks like you found a program that does not exist or the URL was mistyped by accident.')}
  />
);

export const ContractNotFound = () => (
  <NotFound
    description={_t('Looks like you found an contract that does not exist or the URL was mistyped by accident.')}
  />
);

export const NoActiveContractsFound = () => (
  <NotFound
    description={_t(
      'Your access to Coursera has been discontinued. We recommend reaching out to your Account Manager or our Support team.'
    )}
  >
    <Button component="a" variant="secondary" href="https://business.coursera.help/hc/requests/new ">
      {_t('Contact Support')}
    </Button>
  </NotFound>
);
